// @flow
/* -----------------Globals--------------- */
import React from 'react';

/* -----------------UI--------------- */
import {
  AppBar,
  Button,
  Grid,
  IconButton,
  Container,
  Tooltip,
  Typography,
  Box,
  useMediaQuery,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import {
  Close,
  CheckAll,
  FolderPlusOutline,
  Heart,
  HeartOutline,
  FolderSearchOutline,
  InformationOutline,
} from 'mdi-material-ui';

/* -----------------Types--------------- */
import type {
  ContentTypes,
} from 'common/types';
import type {
  AddToFolderAction,
} from './types';
import type {
  TBreadcrumbs,
} from './appbarutils';

/* -----------------Utils--------------- */
import {
  standardIcons as getStandardIcons,
  standardIconType,
} from 'common/content/appbarutils';
import {
  profileIcons,
} from './appbarutils';
/* -----------------Child components--------------- */
import Link from './link';
import Breadcrumbs from './Breadcrumbs';

/* -----------------Styles--------------- */
const styles = theme => ({
  active: {},
  normalContainer: {
    paddingRight: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 900,
    height: 56,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    paddingTop: 4,
    paddingBottom: 4,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      paddingRight: 12,
      paddingLeft: 12,
    },
  },
  positionSticky: {
    top: theme.spacing(8),
    zIndex: 10,
  },
  title: {
    fontSize: 18,
  },
  grow: {
    flexGrow: 1,
  },
  gridItem: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.primary.contrastText,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  breadcrumbsContainer: {
    flexGrow: 1,
  },
  breadcrumbsIcon: {
    height: 20,
    width: 20,
    marginRight: -theme.spacing(1),
    marginLeft: -theme.spacing(1),
  },
  breadcrumbsLink: {
    textDecoration: 'none',
    fontWeight: '400',
    color: theme.palette.secondary.light,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  button: {
    margin: 0,
  },
  heartFilled: {
    color: theme.palette.error.main,
  },
});

/* -----------------Props--------------- */
type Props = {
  classes: *,
  title?: string,
  numSelected?: number,
  favorited?: boolean,
  contentType?: ContentTypes,
  inReorderingMode?: boolean,
  variant?: "content" | "profile" | "schedule" | "sticky",
  handleAppbarAction: (type: string) => void,
  handleAddToFolder: AddToFolderAction,
  showSelectAll?: boolean,
  breadcrumbs: TBreadcrumbs[],
  isAuthenticated: boolean,
  renderContentMenu: () => void,
};

// constant
const iconMap = {
  close: <Close />,
  'check-all': <CheckAll />,
  heart: <Heart />,
  'heart-outline': <HeartOutline />,
  'folder-search-outline': <FolderSearchOutline />,
  'information-outline': <InformationOutline />,

};

/* -----------------Component--------------- */
const Appbar = (props: Props) => {
  const {
    classes,
    numSelected = 0,
    favorited,
    contentType,
    variant,
    handleAppbarAction,
    inReorderingMode,
    showSelectAll = true,
    handleAddToFolder,
    renderContentMenu,
    isAuthenticated,
    breadcrumbs = [],
  } = props;

  const selected = numSelected > 0;

  const titleSelected = inReorderingMode ? 'Drag to re-order'
    : `${numSelected} item${numSelected > 1 ? 's' : ''}`;

  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const standardIcons = React.useMemo(() => getStandardIcons(
    favorited,
    contentType,
    variant,
    handleAppbarAction,
    true,
    isAuthenticated,
  ).filter(({ type }) => type !== standardIconType.back
    && type !== standardIconType.menu), [contentType, favorited,
    handleAppbarAction, isAuthenticated, variant]);

  const showBreadcrumbs = variant !== 'profile' && !isMobile;
  const contentsNormal = (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      flex={1}
    >

      {showBreadcrumbs ? (
        <Breadcrumbs
          breadcrumbValues={
            breadcrumbs
          }
        />
      ) : (
        <Grid
          item
        />
      )}

      <Grid item className={classes.gridItem}>
        {variant !== 'profile'
          ? standardIcons.map(({
            type,
            name,
            action,
            tooltip = '',
          }) => (
            <Tooltip
              title={tooltip}
              enterDelay={300}
              key={type}
            >
              <IconButton
                onClick={() => {
                  action(type);
                }}
              >
                {favorited && type === standardIconType.fav ? (
                  <Heart className={classes.heartFilled} />
                ) : (
                  name && iconMap[`${name}`]
                )}
              </IconButton>
            </Tooltip>
          )) : (
            <>
              {profileIcons.map((item, index) => (
                <React.Fragment key={index.toString()}>
                  {item.title ? (
                    <Tooltip
                      title={item.title}
                      enterDelay={300}
                      key={item.title}
                    >
                      <Link href="/settings" hrefAs="/settings">
                        <IconButton
                          edge="end"
                        >
                          {item.icon}
                        </IconButton>
                      </Link>
                    </Tooltip>
                  ) : <IconButton>{item.icon}</IconButton>}
                </React.Fragment>
              ))}
            </>
          )}
        {renderContentMenu ? renderContentMenu() : null}
      </Grid>
    </Box>
  );
  const renderMenuButton = (
    action,
  ) => (
    <Grid item className={classes.gridItem}>
      <IconButton
        edge="start"
        className={classes.menuButton}
        color="inherit"
        aria-label="close"
        onClick={action}
      >
        <Close />
      </IconButton>
      <Typography
        className={classes.title}
        variant="h5"
        noWrap
      >
        {titleSelected}
      </Typography>
    </Grid>
  );
  const contentsReordering = (
    <>
      {renderMenuButton(() => handleAppbarAction('close'))}
      <Grid item className={classes.gridItem}>
        <Button
          variant="contained"
          className={classes.button}
          onClick={() => handleAppbarAction('close')}
        >
          Done
        </Button>
      </Grid>
    </>
  );
  const contentsSelected = () => (
    <>
      {renderMenuButton(() => handleAppbarAction('close'))}
      <Grid item className={classes.gridItem}>
        {showSelectAll ? (
          <Tooltip title="Select all" enterDelay={300}>
            <IconButton
              aria-label="check all"
              color="inherit"
              onClick={() => handleAppbarAction('checkall')}
            >
              <CheckAll />
            </IconButton>
          </Tooltip>
        ) : null}
        <Tooltip title="Add to folder" enterDelay={300}>
          <IconButton
            aria-label="add to folder"
            color="inherit"
            onClick={handleAddToFolder}
          >
            <FolderPlusOutline />
          </IconButton>
        </Tooltip>
        {renderContentMenu()}
      </Grid>
    </>
  );

  const renderContents = () => {
    if (selected) {
      return contentsSelected();
    }
    if (inReorderingMode) {
      return contentsReordering;
    }
    return contentsNormal;
  };
  const appbarNormal = !selected && !inReorderingMode;
  return (
    <>
      <AppBar
        elevation={0}
        classes={{
          colorDefault: classes.normalContainer,
          positionSticky: classes.positionSticky,
        }}
        color={appbarNormal ? 'default' : 'secondary'}
        position={appbarNormal ? 'relative' : 'sticky'}
      >
        <Container maxWidth="lg" classes={{ root: classes.container }}>
          <Grid container justifyContent="space-between">
            {renderContents()}
          </Grid>
        </Container>
      </AppBar>
    </>
  );
};

export default withStyles(styles)(Appbar);
